import { groupBy } from "lodash";
import { useCallback, useMemo } from "react";

import {
  canAddMultipleTagsForTagType,
  getUpdatingStateForAllTagsOfType,
} from "../TagsEditor.util";
import {
  isRemoveTagAction,
  tagFromSelectOperation,
} from "./TagSelector.change";

export const useTagSelectorActions = ({
  loading,
  existingTags,
  setNewTag,
  tagTypesConfig,
  removeTag,
  setError,
  addTag,
  setTypedValue,
  clearSearchTerm,
  tagsUpdating,
  setTagsUpdating,
  isMulti,
}) => {
  const tagsByType = useMemo(
    () => groupBy(existingTags, "type"),
    [existingTags]
  );

  const setTagUpdateState = useCallback(
    (tag, state) => {
      const tagType = tag?.type;
      const isMultiForTag = canAddMultipleTagsForTagType(
        tagType,
        tagTypesConfig
      );

      if (!isMultiForTag) {
        const allTagsOfType = getUpdatingStateForAllTagsOfType(
          tagsByType,
          tagType,
          state
        );
        setTagsUpdating({ ...tagsUpdating, [tag.id]: state, ...allTagsOfType });
        return;
      }
      setTagsUpdating({ ...tagsUpdating, [tag.id]: state });
    },
    [tagTypesConfig, tagsByType, tagsUpdating, setTagsUpdating]
  );

  const reportError = useCallback(
    (err) => {
      if (setError) {
        setError(err.toString());
      }
    },
    [setError]
  );

  const onChange = useCallback(
    async (newValue, operation) => {
      try {
        if (!operation || loading) {
          return;
        }
        const selected = tagFromSelectOperation(operation, newValue, isMulti);
        if (!selected) {
          return;
        }
        const { tagTypeConfig, ...tag } = selected as {
          tagTypeConfig: { isReadonly?: boolean };
          [key: string]: unknown;
        };
        if (isRemoveTagAction(operation.action)) {
          if (tagTypeConfig.isReadonly) {
            return;
          }
          setTagUpdateState(tag, true);
          await removeTag(tag, tagTypeConfig);
          setTagUpdateState(tag, false);
          return;
        }
        setTagUpdateState(tag, true);
        await setNewTag(tag, tagTypeConfig);
        setTagUpdateState(tag, false);
      } catch (err) {
        reportError(err);
        throw err;
      }
    },
    [loading, setTagUpdateState, removeTag, isMulti, setNewTag, reportError]
  );

  const onRemoveTag = useCallback(
    async (tag, tagTypeConfig) => {
      setTagUpdateState(tag, true);
      try {
        await removeTag(tag, tagTypeConfig);
      } catch (err) {
        reportError(err);
      }
      setTagUpdateState(tag, false);
    },
    [removeTag, reportError, setTagUpdateState]
  );

  const onAddTag = useCallback(
    async (tag, tagTypeConfig) => {
      addTag(tag);
      setTagUpdateState(tag, true);
      try {
        await setNewTag(tag, tagTypeConfig);
        setTypedValue("");
        clearSearchTerm();
      } catch (err) {
        reportError(err);
      }
      setTagUpdateState(tag, false);
    },
    [
      addTag,
      setTagUpdateState,
      setNewTag,
      clearSearchTerm,
      reportError,
      setTypedValue,
    ]
  );

  return { onChange, onRemoveTag, onAddTag };
};
